import Notifier from '@components/common/Notifier';
import OrgAutocomplete from './OrgAutocomplete';
import PropTypes from 'prop-types';
import React, {useCallback, useState} from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Stack,
  Switch,
  TextField,
} from '@mui/material';
import {Label} from '@components/common/Label';
import {createOrganization} from '@account/api';
import {trackEvent} from '@account/analytics';
import {useMutation} from '@tanstack/react-query';

const CreateNewOrgModal = ({
  contractId,
  handlePostOrgCreation,
  handleModalClose,
  setSuccessMessage,
}) => {
  const [orgName, setOrgName] = useState('');
  const [parentOrgId, setParentOrgId] = useState(null);
  const [isSubOrg, setIsSubOrg] = useState(false);

  const {
    mutate: createNewOrganization,
    isLoading: isLoadingCreateResponse,
    error: createOrganizationError,
  } = useMutation(createOrganization, {
    onSuccess: async newOrganization => {
      await handlePostOrgCreation(newOrganization);
      handleModalClose();
      setSuccessMessage(
        <>
          Organization <strong>{newOrganization?.name}</strong> was created.
        </>
      );
    },
  });

  const handleParentOrgSelectionChange = useCallback(
    organization => setParentOrgId(organization?.id),
    [setParentOrgId]
  );

  const handleSubOrgCheckboxChange = useCallback(
    event => {
      setIsSubOrg(event.target.checked);

      trackEvent('Provisioning: New org modal sub-org toggle clicked', {
        contractId,
        newState: event.target.checked,
      });
    },
    [contractId, setIsSubOrg]
  );

  const handleCreateButton = useCallback(
    async ({name, parentOrgId}) =>
      await createNewOrganization({name, parentOrgId}),
    [createNewOrganization]
  );

  return (
    <>
      <Dialog
        data-testid="create-new-organization-modal"
        fullWidth
        maxWidth="xs"
        open
        onClose={handleModalClose}
      >
        <DialogTitle data-testid="dialog-title">
          Create New Organization
        </DialogTitle>

        <DialogContent>
          <Stack marginBottom={2}>
            <Label isInputLabel htmlFor="organization-name">
              {isSubOrg ? 'Sub-Organization Name' : 'Organization Name'}
            </Label>
            <TextField
              autoComplete="off"
              data-testid="organization-name"
              fullWidth
              id="organization-name"
              name="organizationName"
              onChange={e => setOrgName(e.target.value)}
              placeholder="Add an organization name"
              value={orgName}
              variant="outlined"
            />
          </Stack>

          <Stack marginBottom={2}>
            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    checked={isSubOrg}
                    onChange={handleSubOrgCheckboxChange}
                  />
                }
                label="This is a sub-organization"
              />
            </FormGroup>
          </Stack>

          {isSubOrg && (
            <Stack>
              <Label isInputLabel htmlFor="selected-org">
                Parent Organization
              </Label>

              <OrgAutocomplete
                disablePortal={false}
                excludeSuborgs
                onChange={handleParentOrgSelectionChange}
                limit={10}
                showCreateOrgLink={false}
              />
            </Stack>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            color="primary"
            data-testid="cancel-button"
            onClick={handleModalClose}
            size="medium"
            variant="outlined"
          >
            Cancel
          </Button>

          <Button
            color="primary"
            data-testid="create-org-button"
            disabled={
              !orgName || (isSubOrg && !parentOrgId) || isLoadingCreateResponse
            }
            onClick={() => {
              handleCreateButton({
                name: orgName,
                parentOrgId,
              });
            }}
            size="medium"
            variant="contained"
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Notifier
        message="Error creating new organization"
        open={!!createOrganizationError}
        severity="error"
      />
    </>
  );
};

CreateNewOrgModal.propTypes = {
  contractId: PropTypes.number,
  handlePostOrgCreation: PropTypes.func.isRequired,
  handleModalClose: PropTypes.func.isRequired,
  setSuccessMessage: PropTypes.func.isRequired,
};

export default CreateNewOrgModal;
